import { Flex, Text } from '@chakra-ui/react';
import { Checkbox, FormFeedback, OutlineCard } from '@morressier/alembic';
import { FormFieldCheckboxArray, FormFieldTextInput  } from '@publishing-engine/domain/types/article';
import { get } from 'lodash';
import { useFormContext } from 'react-hook-form';
import { FieldLabel } from 'src/components/Field';
import FormFieldTextAreaField from 'src/components/FormField/FormFieldTextArea';
import { ISubmissionFormData } from 'src/contexts/FormContext';

type Props = {
  config: FormFieldCheckboxArray<'preference.dataAvailabilityStatement'>;
  DataAvailabilityTextConfig: FormFieldTextInput<'preference.dataAvailabilityText'> | null;
};

const DataAvailabilityStatement: React.FC<Props> = ({ config, DataAvailabilityTextConfig }) => {
  const {
    formState: { errors },
    watch,
    setValue,
    clearErrors
  } = useFormContext<ISubmissionFormData>();

  const {
    params: { validations, labels },
    caseDataFields: { main: field }
  } = config;

  const value = (watch(field as any) as string[]) || [];
  const error = get(errors, field);

  const options = Object.entries(labels.options || {}).map(([key, opt]) => ({
    id: key,
    main: opt.main,
    secondary: opt.secondary
  }));

  const handleCheckboxChange = (checked: boolean, itemValue: string) => {
    clearErrors(field as any);

    const EXCLUSIVE_VALUES = ['no_new_data', 'no_data_available'];

    let newValue: string[] = [];

    if (checked) {
      if (EXCLUSIVE_VALUES.includes(itemValue)) {
        newValue = [itemValue];
      } else {
        newValue = Array.from(
          new Set([...value.filter(v => !EXCLUSIVE_VALUES.includes(v)), itemValue])
        );
      }
    } else {
      newValue = value.filter(v => v !== itemValue);
    }

    setValue(field as any, newValue, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true
    });
  };

  return (
    <OutlineCard width="100%" padding={4}>
      <Flex flexDirection="column" gap={4} width="100%">
        <FieldLabel
          isRequired={validations.required}
          label={labels.main}
          subText={labels.subtitle}
          helpText={labels.helpText}
        />

        <Flex flexDirection="column" alignItems="flex-start" gap={4}>
          {options.map(item => (
            <Checkbox
              key={item.id}
              ariaLabel={item.main}
              id={`${field}-${item.id}`}
              name={field}
              isChecked={value.includes(item.id)}
              onChange={({ target: { checked } }) =>
                handleCheckboxChange(checked, item.id)
              }
            >
              <Text as="span" textStyle="textBody">
                <Text as="span" fontWeight="semibold">
                  {item.main.replace(/\.$/, '')}:
                </Text>{' '}
                {item.secondary}
              </Text>
            </Checkbox>
          ))}

          {DataAvailabilityTextConfig &&  (
            <FormFieldTextAreaField
              config={DataAvailabilityTextConfig}
              placeholder="Provide any additional details"
            />
          )}
        </Flex>

        {error && (
          <FormFeedback variant="error">
            {'Please select the Data Availability Statement'}
          </FormFeedback>
        )}
      </Flex>
    </OutlineCard>
  );
};

export default DataAvailabilityStatement;
